import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';

const MOTION_OPTIONS = {
  duration: 300,
  easing: 'cubic-bezier(0.2, 0.72, 0.2, 1)',
};

const prefersReducedMotion = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

/**
 * Animates a programmatic pin/unpin reorder from the row's previous position.
 * Drag-and-drop already supplies its own motion, so callers explicitly arm only
 * the state changes which would otherwise look like an unexplained jump.
 */
const useOrderedCollectionMotion = (itemKeys) => {
  const containerRef = useRef(null);
  const previousPositionsRef = useRef(new Map());
  const previousOrderRef = useRef([]);
  const animationRequestRef = useRef(null);
  const signature = useMemo(() => itemKeys.join('\u001f'), [itemKeys]);

  const animateNextChange = useCallback((request = {}) => {
    animationRequestRef.current = request;
  }, []);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const currentPositions = new Map();
    const rows = container.querySelectorAll('[data-collection-key]');
    rows.forEach((row) => {
      currentPositions.set(row.dataset.collectionKey, row.getBoundingClientRect());
    });

    const animationRequest = animationRequestRef.current;
    if (animationRequest && !prefersReducedMotion()) {
      rows.forEach((row) => {
        const itemKey = row.dataset.collectionKey;
        if (animationRequest.itemKey && animationRequest.itemKey !== itemKey) return;

        let previous = previousPositionsRef.current.get(itemKey);
        if (
          animationRequest.itemKey === itemKey
          && Number.isInteger(animationRequest.fromIndex)
        ) {
          const dropSlotKey = previousOrderRef.current[animationRequest.fromIndex];
          previous = previousPositionsRef.current.get(dropSlotKey) || previous;
        }
        const current = currentPositions.get(row.dataset.collectionKey);
        if (!previous || !current || typeof row.animate !== 'function') return;

        const deltaY = previous.top - current.top;
        if (Math.abs(deltaY) < 1) return;
        if (typeof row.getAnimations === 'function') {
          row.getAnimations().forEach(animation => animation.cancel());
        }
        row.animate(
          [
            { transform: `translateY(${deltaY}px)` },
            { transform: 'translateY(0)' },
          ],
          MOTION_OPTIONS,
        );
      });
    }

    animationRequestRef.current = null;
    previousPositionsRef.current = currentPositions;
    previousOrderRef.current = Array.from(currentPositions.keys());
  }, [signature]);

  return { containerRef, animateNextChange };
};

export default useOrderedCollectionMotion;
